import { css } from '@emotion/react';
import { AbilityOutput, GainTargetType } from '@mythos/game/CardAbilities';
import {
  CardAffinity,
  CardType,
  getCardAffinityChar,
  getCardTypeChar,
  isCardType,
} from '@mythos/game/CardTypes';
import { Token } from '@mythos/game/Resources';
import invariant from 'invariant';
import React from 'react';
import { match } from 'ts-pattern';
import { UIColorPalette } from './ColorPalette';
import Symbols, { ResourceToSymbol, SymbolView } from './Symbols';

import TrayDecorationUnitDerion from '../assets/card_frame/tray_decoration_unit_derion.svg';
import TrayDecorationUnitTyros from '../assets/card_frame/tray_decoration_unit_tyros.svg';
import TrayDecorationUnitZeona from '../assets/card_frame/tray_decoration_unit_zeona.svg';

import TrayDecorationBuildingDerion from '../assets/card_frame/tray_decoration_building_derion.svg';
import TrayDecorationBuildingTyros from '../assets/card_frame/tray_decoration_building_tyros.svg';
import TrayDecorationBuildingZeona from '../assets/card_frame/tray_decoration_building_zeona.svg';

import TrayDecorationEventDerion from '../assets/card_frame/tray_decoration_event_derion.svg';
import TrayDecorationEventTyros from '../assets/card_frame/tray_decoration_event_tyros.svg';
import TrayDecorationEventZeona from '../assets/card_frame/tray_decoration_event_zeona.svg';

import TrayDecorationUnitDerionUpgrade from '../assets/card_frame/tray_decoration_unit_derion_upgrade.svg';
import TrayDecorationUnitTyrosUpgrade from '../assets/card_frame/tray_decoration_unit_tyros_upgrade.svg';
import TrayDecorationUnitZeonaUpgrade from '../assets/card_frame/tray_decoration_unit_zeona_upgrade.svg';

import TrayDecorationBuildingDerionUpgrade from '../assets/card_frame/tray_decoration_building_derion_upgrade.svg';
import TrayDecorationBuildingTyrosUpgrade from '../assets/card_frame/tray_decoration_building_tyros_upgrade.svg';
import TrayDecorationBuildingZeonaUpgrade from '../assets/card_frame/tray_decoration_building_zeona_upgrade.svg';

import CornerDecorationDerion from '../assets/card_frame/corner_decoration_derion.svg';
import CornerDecorationTyros from '../assets/card_frame/corner_decoration_tyros.svg';
import CornerDecorationZeona from '../assets/card_frame/corner_decoration_zeona.svg';

export function EffectPill(props: {
  output: AbilityOutput;
  className?: string;
  style?: React.CSSProperties;
}): React.ReactNode | null {
  const output = props.output;

  let prefix: React.ReactNode;
  if (output.multiplier) {
    let midfix: React.ReactNode;
    const count = output.multiplier.resourceDivisor;
    const multiplierSymbolChar = match(output.multiplier.resource)
      .with('card_set', () => {
        invariant(count === 1, 'no asset for N card sets');
        return 'S';
      })
      .with(Token.War, () => {
        if (count !== 1) {
          midfix = <span css={PillStyles.resourceDivisor}>/{count}</span>;
        }
        return 'W';
      })
      .with(Token.Tribute, () =>
        invariant(false, 'no asset for tribute tokens'),
      )
      .otherwise((cardType) => {
        let symbolChar = isCardType(cardType)
          ? getCardTypeChar(cardType)
          : getCardAffinityChar(cardType);

        if (count > 1) {
          invariant(count === 2, 'no asset for N card subtypes');
          symbolChar += symbolChar;
        }
        return symbolChar;
      });
    const multiplierSymbol = MultiplierCharToSymbolImage[multiplierSymbolChar];
    if (!multiplierSymbol) {
      console.warn('no symbol for', multiplierSymbolChar);
      return null;
    }
    prefix = (
      <div css={PillStyles.prefixContainer}>
        <SymbolView
          symbol={multiplierSymbol}
          css={PillStyles.multiplierSymbol}
        />
        {midfix}
      </div>
    );
  }

  return (
    <div
      css={PillStyles.container}
      className={props.className}
      style={{
        ...props.style,
      }}
    >
      {prefix}
      {prefix ? <div css={PillStyles.x}>×</div> : null}
      <SymbolWithNumber
        symbol={ResourceToSymbol[output.outputResource]}
        number={output.outputRatio}
      />
    </div>
  );
}

export function SymbolWithEmbeddedSymbol(props: {
  className?: string;
  style?: React.CSSProperties;
  outerSymbol: Symbols;
  outerSymbolHeight: number;
  embeddedSymbol: Symbols;
  embeddedSymbolHeight: number;
  embeddedSymbolStyle?: React.CSSProperties;
}) {
  return (
    <div
      css={PillStyles.symbolWithNumberContainer}
      className={props.className}
      style={{
        color: UIColorPalette.symbolWithNumberText,
        ...props.style,
      }}
    >
      <SymbolView
        symbol={props.outerSymbol}
        css={PillStyles.symbol}
        style={{ height: props.outerSymbolHeight }}
      />
      <SymbolView
        symbol={props.embeddedSymbol}
        css={PillStyles.embeddedSymbol}
        style={{
          height: props.embeddedSymbolHeight,
          ...props.embeddedSymbolStyle,
        }}
      />
    </div>
  );
}

export function SymbolWithNumber(props: {
  className?: string;
  style?: React.CSSProperties;
  symbol: Symbols;
  number: number | string;
  height?: number;
  fontSize?: number;
}) {
  return (
    <div
      css={PillStyles.symbolWithNumberContainer}
      className={props.className}
      style={{
        color: UIColorPalette.symbolWithNumberText,
        ...props.style,
      }}
    >
      <SymbolView
        symbol={props.symbol}
        css={PillStyles.symbol}
        style={{ height: props.height }}
      />
      <span
        css={PillStyles.symbolNumber}
        style={{
          fontSize:
            props.fontSize || (props.height ? 0.75 * props.height : undefined),
        }}
      >
        {props.number}
      </span>
    </div>
  );
}

export function EffectTriggerPrefix(props: {
  symbol: Symbols;
  secondSymbol?: Symbols;
}) {
  return (
    <>
      <SymbolView
        symbol={props.symbol}
        css={EffectTriggerStyles.triggerSymbol}
      />
      {props.secondSymbol && (
        <SymbolView
          symbol={props.secondSymbol}
          css={EffectTriggerStyles.triggerSymbol}
        />
      )}
      <span css={EffectTriggerStyles.triggerColon}>:</span>
    </>
  );
}

export function GainOtherTriggerPrefix(props: { targetType: GainTargetType }) {
  return match(props.targetType)
    .with({ target_type: 'tribute' }, () => (
      <EffectTriggerPrefix symbol={Symbols.M3_ACQUIRE_TRIBUTE} />
    ))
    .with({ target_type: 'token' }, (t) => {
      invariant(t.token === Token.War, 'invalid token');
      return <EffectTriggerPrefix symbol={Symbols.ACQUIRE_TOKEN_WAR} />;
    })
    .with({ target_type: 'card_affinity' }, (t) => {
      return (
        <EffectTriggerPrefix
          symbol={Symbols.M3_ACQUIRE}
          secondSymbol={
            MultiplierCharToSymbolImage[getCardAffinityChar(t.card_affinity)]
          }
        />
      );
    })
    .with({ target_type: 'card_type' }, (t) => {
      return (
        <EffectTriggerPrefix
          symbol={Symbols.M3_ACQUIRE}
          secondSymbol={
            MultiplierCharToSymbolImage[getCardTypeChar(t.card_type)]
          }
        />
      );
    })
    .exhaustive();
}

export function getTrayDecorationAssetURL(
  cardType: CardType,
  cardAffinity: CardAffinity,
  isUpgrade: boolean,
) {
  const assetURL = match(cardType)
    .with(CardType.Unit, () =>
      match(cardAffinity)
        .with(CardAffinity.Tyros, () =>
          isUpgrade ? TrayDecorationUnitTyrosUpgrade : TrayDecorationUnitTyros,
        )
        .with(CardAffinity.Derion, () =>
          isUpgrade
            ? TrayDecorationUnitDerionUpgrade
            : TrayDecorationUnitDerion,
        )
        .with(CardAffinity.Zeona, () =>
          isUpgrade ? TrayDecorationUnitZeonaUpgrade : TrayDecorationUnitZeona,
        )
        .otherwise(() => null),
    )
    .with(CardType.Building, () =>
      match(cardAffinity)
        .with(CardAffinity.Tyros, () =>
          isUpgrade
            ? TrayDecorationBuildingTyrosUpgrade
            : TrayDecorationBuildingTyros,
        )
        .with(CardAffinity.Derion, () =>
          isUpgrade
            ? TrayDecorationBuildingDerionUpgrade
            : TrayDecorationBuildingDerion,
        )
        .with(CardAffinity.Zeona, () =>
          isUpgrade
            ? TrayDecorationBuildingZeonaUpgrade
            : TrayDecorationBuildingZeona,
        )
        .otherwise(() => null),
    )
    .with(CardType.Event, () =>
      match(cardAffinity)
        .with(CardAffinity.Tyros, () => TrayDecorationEventTyros)
        .with(CardAffinity.Derion, () => TrayDecorationEventDerion)
        .with(CardAffinity.Zeona, () => TrayDecorationEventZeona)
        .otherwise(() => null),
    )
    .otherwise(() => null);
  return assetURL;
}

export function getCornerDecorationAssetURL(
  cardAffinity: CardAffinity,
): string | null {
  return match(cardAffinity)
    .with(CardAffinity.Derion, () => CornerDecorationDerion)
    .with(CardAffinity.Tyros, () => CornerDecorationTyros)
    .with(CardAffinity.Zeona, () => CornerDecorationZeona)
    .otherwise(() => null);
}

const PillStyles = {
  container: css({
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',

    position: 'relative',

    borderRadius: '12px',
  }),
  pillChrome: css({
    '&::before': {
      position: 'absolute',
      content: '""',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      borderRadius: '12px',
    },
  }),
  prefixContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 1,
    marginRight: 1,
  }),
  text: css({
    marginLeft: 2.5,
    marginRight: 0.5,
    marginBottom: -1,

    fontSize: 10,
    fontWeight: 300,
  }),
  symbol: css({
    height: 12,
    width: 'auto',
    margin: 0,
  }),
  embeddedSymbol: css({
    width: 'auto',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  }),
  multiplierSymbol: css({
    height: 8,
    width: 'auto',
  }),
  resourceDivisor: css({
    fontSize: 4,
    fontWeight: 'bold',
    marginTop: 3,
    marginLeft: -1,
  }),
  x: css({
    fontSize: 6,
    marginTop: -1,
    marginBottom: -1,
    color: UIColorPalette.effectPillXText,
  }),
  outputRatio: css({
    fontSize: 8,
    marginTop: 1,
  }),
  symbolWithNumberContainer: css({
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    boxSizing: 'border-box',
  }),
  SymbolWithEmbeddedSymbolContainer: css({
    position: 'relative',
  }),
  symbolNumber: css({
    fontSize: 6,
    fontFamily: 'Mythos-CardTitle',
    fontWeight: 600,
    position: 'absolute',
    right: '50%',
    top: '50%',
    transform: 'translate(50%, -50%)',
  }),
} as const;
const EffectTriggerStyles = {
  triggerSymbol: css({
    height: 9,
    width: 'auto',
    marginRight: 0,
    marginLeft: 1,
  }),
  triggerColon: css({
    fontSize: 7.5,
    marginRight: 1,
    marginBottom: 1.5,
    verticalAlign: 'middle',
  }),
};

const MultiplierCharToSymbolImage: Record<string, Symbols> = {
  D: Symbols.CARD_AFFINITY_DERION,
  T: Symbols.CARD_AFFINITY_TYROS,
  Z: Symbols.CARD_AFFINITY_ZEONA,

  A: Symbols.CARD_AFFINITY_BASIC,

  S: Symbols.CARD_SET,

  B: Symbols.CARD_TYPE_BUILDING,
  U: Symbols.CARD_TYPE_UNIT,
  E: Symbols.CARD_TYPE_EVENT,

  W: Symbols.TOKEN_WAR,
};

export const AffinityOrTypeCharToProphecySymbolImage: Record<string, Symbols> =
  {
    [CardAffinity.Derion]: Symbols.CARD_AFFINITY_DERION_PROPHECY1,
    [CardAffinity.Tyros]: Symbols.CARD_AFFINITY_TYROS_PROPHECY1,
    [CardAffinity.Zeona]: Symbols.CARD_AFFINITY_ZEONA_PROPHECY1,

    [CardType.Unit]: Symbols.CARD_TYPE_UNIT_PROPHECY1,
    [CardType.Building]: Symbols.CARD_TYPE_BUILDING_PROPHECY1,
    [CardType.Event]: Symbols.CARD_TYPE_EVENT_PROPHECY1,
  };
